import React, { useState } from 'react';
import { X, Star, Send } from 'lucide-react';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Textarea';
import toast from 'react-hot-toast';
import { ticketService } from '../../api/ticketService';

/**
 * Feedback Popup Component
 * 
 * Displays a popup for users to provide feedback on closed tickets.
 * Shows ticket number and subject, with 5-star rating and comment box.
 */
const FeedbackPopup = ({ ticket, onClose, onSubmitted }) => {
  const [starsCount, setStarsCount] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStarClick = (rating) => {
    setStarsCount(rating);
  };

  const handleStarHover = (rating) => {
    setHoveredStar(rating);
  };

  const handleStarLeave = () => {
    setHoveredStar(0);
  };

  const handleSubmit = async () => {
    if (starsCount === 0) {
      toast.error('Please provide a rating by selecting stars');
      return;
    }

    if (!ticket || !ticket.ticketId) {
      toast.error('Invalid ticket information');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await ticketService.submitFeedback({
        ticketId: ticket.ticketId,
        starsCount: starsCount,
        comment: comment.trim() || null
      });

      if (response?.data?.success) {
        toast.success('Thank you for your feedback!');
        if (onSubmitted) {
          onSubmitted(ticket.ticketId);
        }
        onClose();
      } else {
        throw new Error(response?.data?.message || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!ticket) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative min-w-0 max-w-md w-full overflow-hidden rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="mb-6 min-w-0 pr-10">
          <h2 className="mb-2 max-w-full break-words text-2xl font-bold text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
            {ticket.ticketNumber || `Ticket #${ticket.ticketId}`}
          </h2>
          <p className="max-w-full break-words text-sm text-gray-600 [overflow-wrap:anywhere] dark:text-gray-400 whitespace-pre-wrap">
            {ticket.subject || 'No subject'}
          </p>
        </div>

        {/* Star Rating */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Rate your experience
          </label>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => handleStarClick(star)}
                onMouseEnter={() => handleStarHover(star)}
                onMouseLeave={handleStarLeave}
                className="focus:outline-none transition-transform hover:scale-110"
                aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
              >
                <Star
                  className={`h-8 w-8 ${
                    star <= (hoveredStar || starsCount)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300 dark:text-gray-600'
                  } transition-colors`}
                />
              </button>
            ))}
            {starsCount > 0 && (
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                {starsCount} {starsCount === 1 ? 'star' : 'stars'}
              </span>
            )}
          </div>
        </div>

        {/* Comment Box */}
        <div className="mb-6">
          <label
            htmlFor="feedback-comment"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Comments (optional)
          </label>
          <Textarea
            id="feedback-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your thoughts about the support you received..."
            rows={4}
            className="w-full"
            maxLength={500}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {comment.length}/500 characters
          </p>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Close
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || starsCount === 0}
            className="flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Submit Feedback
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPopup;

